import { Check, MessageCircle, Search, UserPlus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Friendship, FriendshipWithProfiles, Profile } from '../types';
import { getSupabase } from '../lib/supabase';
import Avatar from './Avatar';

export default function FriendRequests({
  currentProfile,
  onlineIds,
  onConversationCreated,
}: {
  currentProfile: Profile;
  onlineIds: Set<string>;
  onConversationCreated: (conversationId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Profile[]>([]);
  const [friendships, setFriendships] = useState<FriendshipWithProfiles[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const loadFriendships = useCallback(async () => {
    const client = getSupabase();
    const { data } = await client
      .from('friendships')
      .select('*')
      .or(`requester_id.eq.${currentProfile.id},addressee_id.eq.${currentProfile.id}`)
      .order('updated_at', { ascending: false });

    const rows = (data ?? []) as Friendship[];
    const userIds = [...new Set(rows.flatMap((friendship) => [friendship.requester_id, friendship.addressee_id]))];
    const profiles = new Map<string, Profile>();

    if (userIds.length) {
      const { data: profileRows } = await client.from('profiles').select('*').in('id', userIds);
      ((profileRows ?? []) as Profile[]).forEach((profile) => profiles.set(profile.id, profile));
    }

    setFriendships(
      rows.map((friendship) => ({
        ...friendship,
        requester: profiles.get(friendship.requester_id) ?? null,
        addressee: profiles.get(friendship.addressee_id) ?? null,
      })),
    );
  }, [currentProfile.id]);

  useEffect(() => {
    loadFriendships();
  }, [loadFriendships]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const timeout = window.setTimeout(async () => {
      const client = getSupabase();
      setLoading(true);
      const term = query.trim().replace(/[%_]/g, '');
      const { data, error } = await client
        .from('profiles')
        .select('*')
        .or(`username.ilike.%${term}%,display_name.ilike.%${term}%`)
        .limit(10);

      setLoading(false);
      if (error) {
        setMessage(error.message);
        return;
      }

      setResults(((data ?? []) as Profile[]).filter((profile) => profile.id !== currentProfile.id));
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [currentProfile.id, query]);

  const pendingIncoming = useMemo(
    () =>
      friendships.filter(
        (friendship) => friendship.status === 'pending' && friendship.addressee_id === currentProfile.id,
      ),
    [currentProfile.id, friendships],
  );

  const friends = useMemo(
    () => friendships.filter((friendship) => friendship.status === 'accepted'),
    [friendships],
  );

  async function sendRequest(addresseeId: string) {
    const client = getSupabase();
    setMessage(null);
    const { error } = await client.from('friendships').insert({
      requester_id: currentProfile.id,
      addressee_id: addresseeId,
      status: 'pending',
    });

    if (error) {
      setMessage(error.message);
      return;
    }

    setMessage('Friend request sent.');
    await loadFriendships();
  }

  async function updateFriendship(id: string, status: 'accepted' | 'blocked') {
    const client = getSupabase();
    const { error } = await client.from('friendships').update({ status }).eq('id', id);
    if (error) setMessage(error.message);
    await loadFriendships();
  }

  async function startDirectMessage(otherUserId: string) {
    const client = getSupabase();
    const { data, error } = await client.rpc('create_direct_conversation', { other_user_id: otherUserId });
    if (error) {
      setMessage(error.message);
      return;
    }
    onConversationCreated(data);
  }

  function otherProfile(friendship: FriendshipWithProfiles) {
    return friendship.requester_id === currentProfile.id ? friendship.addressee : friendship.requester;
  }

  return (
    <main className="relay-scrollbar h-screen min-w-0 flex-1 overflow-y-auto bg-ink-850 p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Friends</h1>
          <p className="mt-1 text-sm text-slate-400">Search users, manage requests, and start direct messages.</p>
        </div>

        <section className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full rounded-lg border border-white/10 bg-black/25 py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-500"
              placeholder="Search users"
            />
          </label>
          {message && <p className="mt-3 text-sm text-relay-cyan">{message}</p>}
          {loading && <p className="mt-4 text-sm text-slate-500">Searching...</p>}
          {results.length > 0 && (
            <div className="mt-4 divide-y divide-white/10">
              {results.map((profile) => (
                <div key={profile.id} className="flex items-center gap-3 py-3">
                  <Avatar name={profile.display_name} src={profile.avatar_url} online={onlineIds.has(profile.id)} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{profile.display_name}</p>
                    <p className="truncate text-xs text-slate-500">@{profile.username}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => sendRequest(profile.id)}
                    className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                  >
                    <UserPlus size={15} />
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => startDirectMessage(profile.id)}
                    className="inline-flex items-center gap-2 rounded-lg bg-relay-blue px-3 py-2 text-xs font-semibold text-white transition hover:brightness-110"
                  >
                    <MessageCircle size={15} />
                    Message
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {pendingIncoming.length > 0 && (
          <section className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Friend requests</h2>
            <div className="divide-y divide-white/10">
              {pendingIncoming.map((friendship) => {
                const requester = friendship.requester;
                if (!requester) return null;
                return (
                  <div key={friendship.id} className="flex items-center gap-3 py-3">
                    <Avatar name={requester.display_name} src={requester.avatar_url} online={onlineIds.has(requester.id)} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white">{requester.display_name}</p>
                      <p className="truncate text-xs text-slate-500">@{requester.username}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateFriendship(friendship.id, 'accepted')}
                      className="grid h-9 w-9 place-items-center rounded-lg bg-relay-green/20 text-relay-green"
                      aria-label="Accept friend request"
                    >
                      <Check size={17} />
                    </button>
                    <button
                      type="button"
                      onClick={() => updateFriendship(friendship.id, 'blocked')}
                      className="grid h-9 w-9 place-items-center rounded-lg bg-relay-red/15 text-relay-red"
                      aria-label="Block request"
                    >
                      <X size={17} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Accepted friends</h2>
          {friends.length === 0 ? (
            <p className="text-sm text-slate-500">Accepted friends will appear here.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {friends.map((friendship) => {
                const friend = otherProfile(friendship);
                if (!friend) return null;
                return (
                  <button
                    key={friendship.id}
                    type="button"
                    onClick={() => startDirectMessage(friend.id)}
                    className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/15 p-3 text-left transition hover:bg-white/[0.06]"
                  >
                    <Avatar name={friend.display_name} src={friend.avatar_url} online={onlineIds.has(friend.id)} />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-white">{friend.display_name}</span>
                      <span className="text-xs text-slate-500">@{friend.username}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

